import React, { useState, useRef, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCheck, faSearch } from "@fortawesome/free-solid-svg-icons";
import { useEscapeKey } from "../hooks/useEscapeKey";
import "../styles/GlobalFilters.css";

// Popup value-picker for a Global Filter card — replaces the native
// <select>/<select multiple> with a searchable checklist popup, since a
// browser multi-select box is cramped and easy to fat-finger with long
// option lists.
export default function FilterOptionsPicker({ options = [], isMulti, selectedValue, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  const selectedArr = Array.isArray(selectedValue)
    ? selectedValue
    : selectedValue
    ? [selectedValue]
    : [];

  useEscapeKey(open, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const filteredOptions = useMemo(
    () => options.filter((opt) => (opt.Name || "").toLowerCase().includes(search.toLowerCase())),
    [options, search]
  );

  const summaryLabel = () => {
    if (!selectedArr.length) return "All";
    if (!isMulti) {
      const opt = options.find((o) => o.Value === selectedArr[0]);
      return opt?.Name || selectedArr[0];
    }
    if (selectedArr.length === 1) {
      const opt = options.find((o) => o.Value === selectedArr[0]);
      return opt?.Name || selectedArr[0];
    }
    return `${selectedArr.length} selected`;
  };

  const handlePick = (value) => {
    if (isMulti) {
      const next = selectedArr.includes(value)
        ? selectedArr.filter((v) => v !== value)
        : [...selectedArr, value];
      onChange(next);
    } else {
      onChange(value);
      setOpen(false);
    }
  };

  return (
    <div className="gf-options-picker" ref={ref}>
      <button
        type="button"
        className={`gf-options-trigger ${open ? "gf-options-trigger--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="gf-options-trigger-label">{summaryLabel()}</span>
        <FontAwesomeIcon icon={faChevronDown} className="gf-options-trigger-arrow" />
      </button>

      {open && (
        <div className="gf-options-popup">
          {options.length > 6 && (
            <div className="gf-options-search">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder="Search options..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          )}

          <div className="gf-options-list">
            {/* "All" = nothing explicitly selected — same as this filter's
                untouched/default state. Always shown, own row, unaffected
                by search, for both single- and multi-select. */}
            <button
              type="button"
              className={`gf-options-item gf-options-item--all ${!selectedArr.length ? "gf-options-item--active" : ""}`}
              onClick={() => { onChange(isMulti ? [] : ""); setOpen(false); }}
            >
              <span className="gf-options-item-check">
                {!selectedArr.length && <FontAwesomeIcon icon={faCheck} />}
              </span>
              All
            </button>

            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isChecked = selectedArr.includes(opt.Value);
                return (
                  <button
                    type="button"
                    key={opt.Id}
                    className={`gf-options-item ${isChecked ? "gf-options-item--active" : ""}`}
                    onClick={() => handlePick(opt.Value)}
                  >
                    <span className="gf-options-item-check">
                      {isChecked && <FontAwesomeIcon icon={faCheck} />}
                    </span>
                    {opt.Name}
                  </button>
                );
              })
            ) : (
              <div className="gf-options-empty">No matching options</div>
            )}
          </div>

          {isMulti && (
            <div className="gf-options-footer">
              <button type="button" className="gf-options-done-btn" onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
